export const isValidFingerprint = (fingerprint: unknown) =>
  Boolean(
    fingerprint &&
      typeof fingerprint === 'object' &&
      !Array.isArray(fingerprint) &&
      Object.keys(fingerprint).length > 0
  );

export const canAnalyzeBand = ({
  fingerprint,
  fingerprintStatus,
  recognitionStatus,
  candidates,
}: {
  fingerprint: unknown;
  fingerprintStatus: string;
  recognitionStatus: string;
  candidates: unknown[];
}) =>
  fingerprintStatus === 'complete' &&
  recognitionStatus === 'complete' &&
  isValidFingerprint(fingerprint) &&
  candidates.length >= 2;

const candidateId = (candidate: any) =>
  candidate?.cigarId ?? candidate?.cigar_id ?? candidate?.candidateId;

const adjustmentId = (adjustment: any) =>
  adjustment?.cigarId ??
  adjustment?.cigar_id ??
  adjustment?.candidateId ??
  adjustment?.candidate_id;

const finiteNumber = (...values: unknown[]) => {
  const value = values.find(
    (item) =>
      item !== null &&
      item !== undefined &&
      item !== '' &&
      Number.isFinite(Number(item))
  );
  return value === undefined ? null : Number(value);
};

const adjustedScore = (candidate: any, adjustment: any) => {
  const explicitScore = finiteNumber(
    adjustment?.adjustedScore,
    adjustment?.adjusted_score,
    adjustment?.finalScore,
    adjustment?.final_score
  );
  if (explicitScore !== null) return explicitScore;

  const originalScore = finiteNumber(
    candidate?.bandOriginalScore,
    candidate?.score,
    candidate?.confidence,
    candidate?.similarity,
    adjustment?.score,
    adjustment?.originalScore,
    adjustment?.original_score
  );
  const scoreAdjustment = finiteNumber(
    adjustment?.scoreAdjustment,
    adjustment?.score_adjustment,
    adjustment?.adjustment
  );
  if (originalScore !== null && scoreAdjustment !== null) {
    return originalScore + scoreAdjustment;
  }
  return originalScore ?? scoreAdjustment;
};

const applyAdjustment = (candidate: any, adjustment: any) => {
  const score = adjustedScore(candidate, adjustment);
  const originalScore = finiteNumber(
    candidate?.bandOriginalScore,
    candidate?.score,
    candidate?.confidence,
    candidate?.similarity,
    adjustment?.score,
    adjustment?.originalScore,
    adjustment?.original_score
  );
  const next = {
    ...candidate,
    ...(originalScore !== null ? { bandOriginalScore: originalScore } : {}),
    bandScoreAdjustment:
      adjustment?.scoreAdjustment ??
      adjustment?.score_adjustment ??
      adjustment?.adjustment,
  };
  if (score !== null) {
    next.score = score;
    if (candidate?.confidence !== undefined) next.confidence = score;
  }
  return next;
};

export const getBandCandidateAdjustments = (result: any): any[] =>
  result?.candidateAdjustments ||
  result?.candidate_adjustments ||
  result?.result?.candidateAdjustments ||
  result?.result?.candidate_adjustments ||
  [];

export const applyBandCandidateAdjustments = (
  candidates: any[] = [],
  adjustments: any[] = []
) => {
  const merged = candidates.map((candidate) => ({ ...candidate }));

  adjustments.forEach((adjustment) => {
    const id = adjustmentId(adjustment);
    if (id === undefined || id === null || id === '') return;
    const existingIndex = merged.findIndex(
      (candidate) => String(candidateId(candidate)) === String(id)
    );

    if (existingIndex >= 0) {
      merged[existingIndex] = applyAdjustment(
        merged[existingIndex],
        adjustment
      );
      return;
    }

    if (adjustment.candidateSource !== 'same_brand_expansion') return;
    merged.push(
      applyAdjustment(
        {
          cigarId: id,
          cigar_id: id,
          brand: adjustment.brand,
          name: adjustment.name,
          candidateSource: 'same_brand_expansion',
          resultType: 'same_brand_suggestion',
        },
        adjustment
      )
    );
  });

  return merged
    .map((candidate, index) => ({ candidate, index }))
    .sort((left, right) => {
      const leftScore = finiteNumber(
        left.candidate.score,
        left.candidate.confidence,
        left.candidate.similarity
      );
      const rightScore = finiteNumber(
        right.candidate.score,
        right.candidate.confidence,
        right.candidate.similarity
      );
      if (leftScore === null && rightScore === null) {
        return left.index - right.index;
      }
      if (leftScore === null) return 1;
      if (rightScore === null) return -1;
      return rightScore - leftScore || left.index - right.index;
    })
    .map(({ candidate }) => candidate);
};
